import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  MultiSelect,
  Select,
  Stack,
  Switch,
  Tabs,
  Text,
  TextInput,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { z } from 'zod';
import { BONUS_TYPES } from '@seo/contracts';
import { ExportButton } from '../components/ExportButton';
import { ParsePanel } from '../components/ParsePanel';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import { BrandComposition } from './BrandComposition';
import { BrandKeywords } from './BrandKeywords';
import { AssetSlots } from '../components/AssetSlots';
import { zodResolver } from '../lib/zod-resolver';
import { CoverageBar } from '../components/CoverageBar';
import { DataTable, type Column } from '../components/DataTable';
import { BrandEditor } from './EntityEditor';
import classes from './EntityPage.module.css';

interface BrandRow {
  id: string;
  name: string;
  foundedYear: number | null;
  platform: string | null;
  holding: string | null;
  originalUrl: string | null;
  licenseName: string | null;
  fillPercent: number;
  bonusCount: number;
  geoCount: number;
  sitesCount: number;
}

/**
 * The gate lives in settings (`tech.entity_fill_threshold_pct`); the screen asks the API for
 * it rather than hardcoding 70, so lowering the threshold changes the badges too.
 */
const FALLBACK_THRESHOLD = 70;

const bonusSchema = z.object({
  type: z.enum(BONUS_TYPES),
  valueTemplate: z.string().min(1, 'Опишіть бонус — саме цей рядок піде у блок'),
  baseAmount: z.string(),
  baseCurrency: z.string(),
  wagering: z.string(),
});

type BonusFormValues = z.infer<typeof bonusSchema>;

/**
 * База брендів (acceptance scenario 2).
 *
 * `fill_percent` is the column the whole screen exists for: it decides whether a build may
 * start at all (§2 step 2). A percentage nobody can explain is a percentage nobody trusts,
 * so opening a brand lists which components are missing and what each is worth.
 */
export function BrandsPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [buildableOnly, setBuildableOnly] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const list = useQuery(
    trpc.brands.list.queryOptions({
      ...(search ? { search } : {}),
      ...(buildableOnly ? { minFill: FALLBACK_THRESHOLD } : {}),
      pagination: { limit: 100, direction: 'asc' },
    }),
  );

  const detail = useQuery({
    ...trpc.brands.byId.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  const readiness = useQuery({
    ...trpc.brands.readiness.queryOptions({ id: selectedId ?? '' }),
    enabled: selectedId !== null,
  });

  const form = useForm<BonusFormValues>({
    initialValues: {
      type: 'welcome',
      valueTemplate: '',
      baseAmount: '',
      baseCurrency: '',
      wagering: '',
    },
    validate: zodResolver(bonusSchema),
  });

  const [removingBonusId, setRemovingBonusId] = useState<string | null>(null);
  /** `null` — «оператор не чіпав», тобто показуємо поточний перелік бренду. */
  const [geoDraft, setGeoDraft] = useState<string[] | null>(null);
  /** Вкладка картки бренду — пʼять станів мокапа замість одного довгого стовпчика. */
  const [brandTab, setBrandTab] = useState<string>('main');
  const canWriteBrands = useCan('databases', 'write');
  /** Масовий парсинг — окреме право: він витрачає токени моделі на весь реєстр одразу. */
  const canExecute = useCan('databases', 'execute');

  const autoparse = useMutation(
    trpc.parsing.autoparseBrands.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: trpc.parsing.pathKey() });
      },
    }),
  );

  const geoOptions = useQuery({
    ...trpc.geo.list.queryOptions({ pagination: { limit: 200, direction: 'asc' } }),
    enabled: selectedId !== null,
  });

  const invalidateBrands = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.brands.pathKey() });
  };

  const removeBonus = useMutation(
    trpc.brands.bonuses.remove.mutationOptions({ onSuccess: invalidateBrands }),
  );
  const setGeos = useMutation(
    trpc.brands.setGeos.mutationOptions({
      onSuccess: async () => {
        setGeoDraft(null);
        await invalidateBrands();
      },
    }),
  );

  const addBonus = useMutation(
    trpc.brands.bonuses.add.mutationOptions({
      onSuccess: async () => {
        form.reset();
        // The percentage is derived, so both the card and the registry row are now stale.
        await queryClient.invalidateQueries({ queryKey: trpc.brands.pathKey() });
      },
    }),
  );

  const threshold = readiness.data?.threshold ?? FALLBACK_THRESHOLD;

  const columns: Column<BrandRow>[] = [
    { key: 'name', header: 'Бренд', render: (row) => <span>{row.name}</span> },
    {
      key: 'license',
      header: 'Ліцензія',
      render: (row) =>
        row.licenseName ? (
          <span>{row.licenseName}</span>
        ) : (
          <Badge color="rose" size="sm" data-testid={`brand-nolicense-${row.name}`}>
            немає
          </Badge>
        ),
    },
    {
      key: 'bonuses',
      header: 'Бонуси',
      align: 'right',
      render: (row) => (
        <span className={classes.mono} data-testid={`brand-bonuses-${row.name}`}>
          {row.bonusCount}
        </span>
      ),
    },
    {
      key: 'geos',
      header: 'ГЕО',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.geoCount}</span>,
    },
    {
      key: 'sites',
      header: 'Сайтів',
      align: 'right',
      render: (row) => <span className={classes.mono}>{row.sitesCount}</span>,
    },
    {
      // The column the screen exists for: below the threshold a build cannot start.
      key: 'fill',
      header: 'Заповнення',
      render: (row) => (
        <Group gap={8} wrap="nowrap" style={{ minWidth: 150 }}>
          <Box style={{ flex: 1 }}>
            <CoverageBar
              value={row.fillPercent}
              threshold={threshold}
              label={`Заповнення бренду ${row.name}`}
            />
          </Box>
          <span className={classes.mono} data-testid={`brand-fill-${row.name}`}>
            {row.fillPercent}%
          </span>
        </Group>
      ),
    },
  ];

  const brand = detail.data;

  return (
    <Stack gap="md">
      <Group gap="md">
        <TextInput
          placeholder="бренд…"
          value={search}
          onChange={(e) => {
            setSearch(e.currentTarget.value);
          }}
          data-testid="brand-search"
          style={{ maxWidth: 260 }}
        />
        <Switch
          size="xs"
          label={`Лише готові до збірки (≥ ${String(threshold)}%)`}
          checked={buildableOnly}
          onChange={(e) => {
            setBuildableOnly(e.currentTarget.checked);
          }}
          data-testid="brand-buildable-only"
        />
        <Text className={classes.dim} data-testid="brand-count">
          {list.data ? `${String(list.data.items.length)} брендів` : ''}
        </Text>

        {/*
          * «AI-заповнити з URL» на весь реєстр — дія мокапа над списком брендів.
          *
          * `parsing.autoparseBrands` існувала без викликача: парсер умів заповнити бренд із
          * його ж сайту, але тільки по одному, з картки. Для сітки в сотні брендів це не
          * інструмент. Процедура сама бере лише тих, у кого заповнений `original_url` — з
          * порожнім сайтом парсити нема звідки.
          *
          * Цілі — описові поля, які не вирішують долю сайту: юрособа, номер ліцензії, реєстр,
          * підтримка, платформа. Керівних полів (ГЕО, статус, привʼязки) у переліку немає
          * свідомо — той самий поділ, що і в алоулисті `PARSABLE_FIELDS`.
          */}
        {canExecute ? (
          <Button
            size="xs"
            variant="default"
            loading={autoparse.isPending}
            data-testid="brand-autoparse"
            onClick={() => {
              autoparse.mutate({
                targets: [
                  'legalEntityFooter',
                  'licenseNumber',
                  'registryUrl',
                  'supportEmail',
                  'platform',
                ],
              });
            }}
          >
            AI-заповнити з URL
          </Button>
        ) : null}
        {autoparse.error === null ? null : (
          <Text className={classes.danger} data-testid="brand-autoparse-error" role="alert">
            {publicMessageOf(autoparse.error)}
          </Text>
        )}
        {autoparse.data === undefined ? null : (
          <Text className={classes.dim} data-testid="brand-autoparse-result">
            запущено прогонів: {autoparse.data.started}
            {autoparse.data.failed.length === 0
              ? ''
              : ` · не вдалося: ${autoparse.data.failed.length}`}
          </Text>
        )}
      </Group>

      {/*
       * Панель створення й картка редагування — один компонент (`BrandEditor`,
       * `./EntityEditor`): до 14.08 тут стояла окрема форма з єдиним полем «назва», і решта
       * дев'ятнадцяти полів мокапу `brandNew` заводилась лише постфактум, через п'ять вкладок
       * нижче. Після успіху `onCreated` одразу відкриває картку щойно створеного бренду — щоб
       * оператор продовжив у бонуси/медіа/кольори, а не впав назад у список.
       */}
      <BrandEditor
        mode="create"
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(id) => {
          setSelectedId(id);
          setBrandTab('main');
        }}
      />

      <DataTable
        rows={list.data?.items ?? []}
        columns={columns}
        loading={list.isPending}
        error={list.error}
        emptyMessage="Брендів немає. Створіть перший — сайт без сутності не збереться."
        onRowClick={(row) => {
          setSelectedId(row.id);
        }}
        footerActions={<ExportButton dataset="brands" zone="databases" />}
        rowTestId={(row) => `brand-row-${row.name}`}
        testId="brand-table"
      />

      {brand === undefined ? null : (
        <BrandEditor
          key={brand.id}
          mode="edit"
          row={{
            id: brand.id,
            name: brand.name,
            foundedYear: brand.foundedYear,
            platform: brand.platform,
            holding: brand.holding,
            originalUrl: brand.originalUrl,
            signupUrl: brand.signupUrl,
            originalLanguage: brand.originalLanguage,
            licenseId: brand.licenseId,
            licenseNumber: brand.licenseNumber,
            registryUrl: brand.registryUrl,
            legalEntityFooter: brand.legalEntityFooter,
          }}
          onClose={() => {
            setSelectedId(null);
          }}
        />
      )}

      {selectedId === null ? null : (
        <Box className={classes.panel} data-testid="brand-detail">
          {detail.isPending ? (
            <Loader size="sm" />
          ) : brand === undefined ? (
            <Text className={classes.danger} data-testid="brand-detail-error">
              {publicMessageOf(detail.error)}
            </Text>
          ) : (
            <Stack gap="md">
              <Group justify="space-between">
                <Text className={classes.panelTitle} data-testid="brand-detail-name">
                  {brand.name}
                </Text>
                <Group gap={8}>
                  <Text
                    className={brand.fill.percent >= threshold ? classes.mono : classes.danger}
                    data-testid="brand-detail-fill"
                  >
                    {brand.fill.percent}%
                  </Text>
                  {brand.fill.percent >= threshold ? (
                    <Badge size="sm" data-testid="brand-buildable">
                      готовий до збірки
                    </Badge>
                  ) : (
                    <Badge color="rose" size="sm" data-testid="brand-not-buildable">
                      збірка заблокована
                    </Badge>
                  )}
                </Group>
              </Group>

              {/*
                * Пʼять вкладок мокапа. До 14.08 картка була одним довгим стовпчиком, і
                * чотири з пʼяти станів інвентаря («Бонуси», «Медіа», «Сторінки», «Ключі»)
                * рахувались як неіснуючі — бо стан, який не можна відкрити окремо, для
                * оператора не існує.
                */}
              <Tabs
                /*
                 * Неактивна вкладка не малюється: `BrandComposition` стоїть у двох панелях
                 * (кольори з соцмережами й типи сторінок), і за типовим `keepMounted` обидві
                 * були б у DOM одночасно — з однаковими `data-testid`. Плюс запити вкладки
                 * «Ключі» не йдуть, поки її не відкрили.
                 */
                keepMounted={false}
                value={brandTab}
                onChange={(value: string | null) => {
                  setBrandTab(value ?? 'main');
                }}
                data-testid="brand-tabs"
              >
                <Tabs.List>
                  <Tabs.Tab value="main" data-testid="brand-tab-main">
                    Основне
                  </Tabs.Tab>
                  <Tabs.Tab value="bonuses" data-testid="brand-tab-bonuses">
                    Бонуси
                  </Tabs.Tab>
                  <Tabs.Tab value="media" data-testid="brand-tab-media">
                    Медіа
                  </Tabs.Tab>
                  <Tabs.Tab value="pages" data-testid="brand-tab-pages">
                    Сторінки
                  </Tabs.Tab>
                  <Tabs.Tab value="kw" data-testid="brand-tab-kw">
                    Ключі
                  </Tabs.Tab>
                </Tabs.List>

                <Tabs.Panel value="main" data-testid="brand-panel-main" pt="sm">
                  <Stack gap="md">
                    <Box>
                      <Text className={classes.dim} mb={6}>
                        Що заповнено — вага показує, скільки коштує кожен компонент
                      </Text>
                      <Stack gap={4}>
                        {brand.fill.components.map((component) => (
                          <Group
                            key={component.key}
                            justify="space-between"
                            data-testid={`fill-${component.key}`}
                            data-filled={component.filled}
                          >
                            <Group gap={8}>
                              <Text className={component.filled ? classes.mono : classes.danger}>
                                {component.filled ? '✓' : '✕'}
                              </Text>
                              <Text size="sm">{component.label}</Text>
                              {component.detail === undefined ? null : (
                                <Text className={classes.dim}>{component.detail}</Text>
                              )}
                            </Group>
                            <Text className={classes.dim}>вага {component.weight}</Text>
                          </Group>
                        ))}
                      </Stack>
                    </Box>
                    <Box>
                      <Text className={classes.dim} mb={6}>
                        ГЕО
                      </Text>
                      <Group gap={6} align="flex-end">
                        {brand.geos.length === 0 ? (
                          <Text className={classes.danger}>Жодного ГЕО</Text>
                        ) : (
                          brand.geos.map((geo) => (
                            <Badge key={geo.id} size="sm" data-testid={`brand-geo-${geo.countryCode}`}>
                              {geo.countryCode}
                            </Badge>
                          ))
                        )}
                        {/*
                         * Перелік ГЕО визначає, де бренд узагалі можна показати, і `setGeos` існував
                         * в API без викликача — тобто після створення бренд лишався з тим набором
                         * країн, який йому дали один раз. Сервер відмовляє звуженій ролі віддати
                         * бренд у чуже ГЕО й окремо відмовляє на порожньому списку.
                         */}
                        {canWriteBrands ? (
                          <>
                            <MultiSelect
                              size="xs"
                              label="Змінити перелік"
                              searchable
                              clearable
                              data={(geoOptions.data?.items ?? []).map((geo) => ({
                                value: geo.id,
                                label: `${geo.countryCode} — ${geo.countryName}`,
                              }))}
                              value={geoDraft ?? brand.geos.map((geo) => geo.id)}
                              onChange={setGeoDraft}
                              data-testid="brand-geo-select"
                              style={{ width: 260 }}
                            />
                            <Button
                              size="compact-xs"
                              loading={setGeos.isPending}
                              data-testid="brand-geo-save"
                              onClick={() => {
                                setGeos.mutate({
                                  id: brand.id,
                                  geoIds: geoDraft ?? brand.geos.map((geo) => geo.id),
                                });
                              }}
                            >
                              Зберегти ГЕО
                            </Button>
                          </>
                        ) : null}
                      </Group>
                      {setGeos.error === null ? null : (
                        <Text className={classes.danger} mt={6} data-testid="brand-geo-error" role="alert">
                          {publicMessageOf(setGeos.error)}
                        </Text>
                      )}
                    </Box>
                    {/* §10: the parser proposes; the policy decides; the snapshot makes it revertible. */}
                    <ParsePanel
                      entityType="brand"
                      entityId={brand.id}
                      targets={['legalEntityFooter', 'licenseNumber', 'originalUrl']}
                    />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="bonuses" data-testid="brand-panel-bonuses" pt="sm">
                  <Stack gap="md">
                    <Box>
                      <Text className={classes.dim} mb={6}>
                        Бонуси — суми зберігаються у базовій валюті, конвертація на збірці
                      </Text>
                      {brand.bonuses.length === 0 ? (
                        <Text className={classes.danger} data-testid="brand-no-bonuses">
                          Бонусів немає — блок бонусів не збереться
                        </Text>
                      ) : (
                        <Stack gap={4}>
                          {brand.bonuses.map((bonus) => (
                            <Group key={bonus.id} gap={10} data-testid={`bonus-${bonus.type}`}>
                              <Badge size="sm" variant="default">
                                {bonus.type}
                              </Badge>
                              <Text size="sm">{bonus.valueTemplate}</Text>
                              {bonus.baseAmount !== null && bonus.baseCurrency !== null ? (
                                <Text className={classes.dim}>
                                  база: {bonus.baseAmount / 100} {bonus.baseCurrency}
                                </Text>
                              ) : null}
                              {bonus.wagering === null ? null : (
                                <Text className={classes.dim}>відіграш {bonus.wagering}</Text>
                              )}
                              {/*
                               * Бонус можна було додати й не можна було прибрати: `bonuses.remove`
                               * існував в API без жодного викликача. Помилковий бонус лишався в
                               * блоці бонусів кожного сайту бренду назавжди.
                               */}
                              {canWriteBrands ? (
                                <Button
                                  size="compact-xs"
                                  variant="subtle"
                                  loading={removeBonus.isPending && removingBonusId === bonus.id}
                                  data-testid={`bonus-remove-${bonus.type}`}
                                  onClick={() => {
                                    setRemovingBonusId(bonus.id);
                                    removeBonus.mutate({ bonusId: bonus.id });
                                  }}
                                >
                                  прибрати
                                </Button>
                              ) : null}
                            </Group>
                          ))}
                        </Stack>
                      )}
                    </Box>
                    <form
                      onSubmit={form.onSubmit((values) => {
                        const amount = values.baseAmount.trim();
                        addBonus.mutate({
                          brandId: brand.id,
                          bonus: {
                            type: values.type,
                            valueTemplate: values.valueTemplate,
                            // Money is stored in minor units — the form takes major units.
                            ...(amount ? { baseAmount: Math.round(Number(amount) * 100) } : {}),
                            ...(values.baseCurrency ? { baseCurrency: values.baseCurrency } : {}),
                            ...(values.wagering ? { wagering: values.wagering } : {}),
                          },
                        });
                      })}
                    >
                      <Group gap="sm" align="flex-start">
                        <Select
                          size="xs"
                          data={BONUS_TYPES.map((t) => ({ value: t, label: t }))}
                          allowDeselect={false}
                          data-testid="bonus-type"
                          style={{ width: 130 }}
                          {...form.getInputProps('type')}
                        />
                        <TextInput
                          size="xs"
                          placeholder="100% до {amount} + {fs} FS"
                          data-testid="bonus-value"
                          style={{ flex: 1, minWidth: 220 }}
                          {...form.getInputProps('valueTemplate')}
                        />
                        <TextInput
                          size="xs"
                          placeholder="сума"
                          data-testid="bonus-amount"
                          style={{ width: 90 }}
                          {...form.getInputProps('baseAmount')}
                        />
                        <TextInput
                          size="xs"
                          placeholder="EUR"
                          data-testid="bonus-currency"
                          style={{ width: 70 }}
                          {...form.getInputProps('baseCurrency')}
                        />
                        <Button
                          size="xs"
                          type="submit"
                          loading={addBonus.isPending}
                          data-testid="bonus-submit"
                        >
                          Додати бонус
                        </Button>
                      </Group>
                      {addBonus.error === null ? null : (
                        <Text className={classes.danger} mt={6} data-testid="bonus-error">
                          {publicMessageOf(addBonus.error)}
                        </Text>
                      )}
                    </form>
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="media" data-testid="brand-panel-media" pt="sm">
                  <Stack gap="md">
                    <BrandComposition
                      brandId={brand.id}
                      colors={brand.colors}
                      socials={brand.socials}
                      pageTypes={brand.pageTypes}
                      sections={['colors', 'socials']}
                    />
                    {/*
                     * Медіа — останні 15 ваги, які досі лише читались бейджами. Слот тепер має
                     * кнопку: байти йдуть у `/media/upload`, який їх санітизує, а в слот кладеться
                     * повернутий `mediaFileId`.
                     */}
                    <AssetSlots brandId={brand.id} slots={brand.assets} />
                  </Stack>
                </Tabs.Panel>

                <Tabs.Panel value="pages" data-testid="brand-panel-pages" pt="sm">
                  <BrandComposition
                    brandId={brand.id}
                    colors={brand.colors}
                    socials={brand.socials}
                    pageTypes={brand.pageTypes}
                    sections={['pageTypes']}
                  />
                </Tabs.Panel>

                <Tabs.Panel value="kw" data-testid="brand-panel-kw" pt="sm">
                  <BrandKeywords brandId={brand.id} />
                </Tabs.Panel>
              </Tabs>
            </Stack>
          )}
        </Box>
      )}
    </Stack>
  );
}
